import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { api } from "../services/api";
import { 
  ChevronLeft, 
  Check, 
  Plus, 
  Image as ImageIcon,
  Info,
  ArrowRight,
  Loader2
} from "lucide-react";

interface Question {
  id: number;
  title: string;
  weight: number;
  image: string | null;
}

const PhysicalDefects: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { deviceId, variantId } = useParams();

  const { groupId } = location.state || {};

  const [questions, setQuestions] = useState<Question[]>([]);
  const [selected, setSelected] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);

  // --- CORE LOGIC (UNTOUCHED) ---
  useEffect(() => {
    if (!groupId) {
      setLoading(false);
      return;
    }
    api.get(`/inspection/group/${groupId}/defect`)
      .then((data) => {
        setQuestions(data || []);
      })
      .catch((err) => {
        console.error("Failed to load defects:", err);
      })
      .finally(() => setLoading(false));
  }, [groupId]);

  const toggle = (id: number) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-3">
        <Loader2 className="w-10 h-10 text-[#363C90] animate-spin" />
        <p className="text-slate-400 text-sm font-bold uppercase tracking-widest italic text-center">Analysing Body <br/> & Screen Defects...</p>
      </div>
    );
  }

  if (!groupId) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center">
        <div className="w-16 h-16 bg-rose-50 rounded-2xl flex items-center justify-center mb-4 text-rose-500">
          <Info size={32} />
        </div>
        <h2 className="text-xl font-black text-slate-800">Session Expired</h2>
        <p className="text-slate-400 text-sm mt-1 mb-6">We lost your diagnostic data. Please restart the evaluation.</p>
        <button 
          onClick={() => navigate(-1)} 
          className="px-8 py-3 bg-[#363C90] text-white rounded-xl font-bold shadow-lg"
        >
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-32">
      
      {/* --- SLIM HEADER --- */}
      <div className="bg-white border-b border-slate-100 sticky top-0 z-30">
        <div className="max-w-[1200px] mx-auto px-4 py-4 md:py-6">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 text-slate-400 hover:text-[#363C90] transition-colors mb-3 text-[10px] font-black uppercase tracking-widest"
          >
            <ChevronLeft size={14} /> Back to Functional Check
          </button>
          
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-black text-slate-900 tracking-tight leading-none">Physical Condition</h1>
              <p className="text-slate-400 text-[13px] font-medium mt-1.5 italic">Select visible issues on the body or screen.</p>
            </div>

            <div className="flex items-center gap-3 bg-slate-50 px-4 py-2.5 rounded-xl border border-slate-100">
              <Info size={16} className="text-[#363C90]" />
              <p className="text-[10px] font-black text-[#363C90] uppercase tracking-wider">Accurate details = Fast Payments</p>
            </div>
          </div>
        </div>
      </div>

      {/* --- GRID LIST (SHARP CARDS) --- */}
      <div className="max-w-[1200px] mx-auto px-4 mt-8">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
          {questions.map((item) => {
            const isActive = selected.includes(item.id);
            return (
              <div
                key={item.id}
                onClick={() => toggle(item.id)}
                className={`
                  group relative cursor-pointer bg-white rounded-2xl p-4 border transition-all duration-200 flex flex-col items-center text-center
                  ${isActive 
                    ? "border-[#363C90] bg-indigo-50/30 shadow-md shadow-indigo-100/50 ring-1 ring-[#363C90]" 
                    : "border-slate-100 hover:border-slate-200 hover:shadow-lg"}
                `}
              >
                {/* Sharp Checkmark Indicator */}
                <div className={`
                  absolute top-3 right-3 w-5 h-5 rounded-lg border flex items-center justify-center transition-all
                  ${isActive ? "bg-[#363C90] border-[#363C90] rotate-0" : "bg-white border-slate-200 rotate-45"}
                `}>
                  {isActive ? <Check size={12} className="text-white" strokeWidth={4} /> : <Plus size={10} className="text-slate-300" />}
                </div>

                <div className="w-14 h-14 md:w-16 md:h-16 mb-3 bg-slate-50 rounded-xl flex items-center justify-center group-hover:bg-white transition-colors overflow-hidden border border-slate-50">
                  {item.image ? (
                    <img src={item.image} alt={item.title} className="w-full h-full object-contain p-2 group-hover:scale-110 transition-transform duration-300" />
                  ) : (
                    <ImageIcon className="text-slate-200" size={24} />
                  )}
                </div>

                <h3 className={`text-[12px] md:text-[13px] font-bold leading-tight px-1 ${isActive ? "text-[#363C90]" : "text-slate-600"}`}>
                  {item.title}
                </h3>
              </div>
            );
          })}
        </div>
        
        {/* If no issues card */}
        <div className="mt-8 p-6 rounded-2xl border-2 border-dashed border-slate-200 bg-white/50 text-center">
            <p className="text-slate-400 text-sm font-bold italic">No issues found? Just click "Continue" below.</p>
        </div>
      </div>

      {/* --- SLIM FLOATING FOOTER --- */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur-md border-t border-slate-100 p-4 z-40">
        <div className="max-w-[1200px] mx-auto flex flex-col md:flex-row items-center justify-between gap-4 px-2">
          <div className="text-center md:text-left">
             <span className="text-[9px] font-black text-slate-400 uppercase tracking-[2px]">Issues Flagged</span>
             <p className="text-lg font-black text-slate-900 leading-none mt-1">{selected.length} Defects Selected</p>
          </div>

          <button
            onClick={() => {
                navigate(`/sell/problems/${deviceId}/${variantId}`, { 
                state: { 
                    deviceId, 
                    variantId, 
                    groupId, 
                    globalAnswers: location.state?.globalAnswers,
                    defectAnswers: selected 
                } 
                });
            }}
            className="w-full md:w-auto px-10 py-4 bg-[#363C90] hover:bg-[#2a2f70] text-white rounded-xl font-black text-sm flex items-center justify-center gap-2 transition-all hover:translate-x-1 shadow-lg shadow-indigo-900/10 uppercase tracking-widest"
            >
            Next: Internal Problems <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PhysicalDefects;